/**
 * MultiCurrencySummary — Phase 5.
 *
 * When a user has line items in more than one currency, this card fetches
 * `/api/financial-plan/summary/global` and renders TWO views side-by-side:
 *
 *   1. **Native breakdown**  — per-currency subtotals (never converted).
 *      Users see their EUR balance as EUR, their JPY consulting income as
 *      JPY, etc. Nothing was rewritten in the database.
 *
 *   2. **Global rollup**     — the single-line "in your currency" total
 *      derived by converting each native bucket to the display currency
 *      using the shared 24 h FX cache. Marked "estimated" with a
 *      timestamp — because live FX rates change, but the STORED VALUES
 *      never do.
 *
 * When every line item shares the plan's currency, this card renders
 * nothing (the standard NetWorthCard already covers it — no visual noise).
 */
import { useEffect, useMemo, useState } from "react";
import { Info, Globe, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useFinancialPlan } from "@/context/FinancialPlanContext";
import { errorMessage } from "@/lib/errors";
import { toast } from "sonner";
import { Money } from "./primitives";

const REL_TIME_UNITS = [
  ["year", 31536000],
  ["month", 2628000],
  ["day", 86400],
  ["hour", 3600],
  ["minute", 60],
];

function formatAge(iso) {
  if (!iso) return "just now";
  const d = new Date(iso);
  const secs = Math.max(0, (Date.now() - d.getTime()) / 1000);
  for (const [label, size] of REL_TIME_UNITS) {
    const v = Math.floor(secs / size);
    if (v >= 1) return `${v} ${label}${v > 1 ? "s" : ""} ago`;
  }
  return "just now";
}

export default function MultiCurrencySummary() {
  const { plan } = useFinancialPlan();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [displayCcy, setDisplayCcy] = useState(null);

  // Show this card only when the user has actually mixed currencies. To
  // detect that without pre-fetching, we peek at plan line-item currencies
  // client-side. If ALL are unset or match the plan currency, skip the fetch.
  const clientHasMixed = useMemo(() => {
    if (!plan) return false;
    const pc = String(plan.currency || "USD").toUpperCase();
    const items = [
      ...(plan.debts || []),
      ...(plan.savings || []),
      ...(plan.investments || []),
      ...(plan.income_sources || []),
      ...(plan.expenses || []),
    ];
    return items.some((it) => {
      const c = String(it?.currency || "").toUpperCase();
      return c && c !== pc;
    });
  }, [plan]);

  useEffect(() => {
    if (!plan || !clientHasMixed) {
      setData(null);
      return;
    }
    fetchRollup(displayCcy);
  }, [plan?.updated_at, clientHasMixed, displayCcy]);

  const fetchRollup = async (ccy) => {
    setLoading(true);
    try {
      const url = ccy
        ? `/financial-plan/summary/global?display_currency=${encodeURIComponent(ccy)}`
        : "/financial-plan/summary/global";
      const { data: d } = await api.get(url);
      setData(d);
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't load the multi-currency rollup."));
    } finally {
      setLoading(false);
    }
  };

  if (!plan || !clientHasMixed) return null;
  if (!data && loading) {
    return (
      <section
        className="bg-card border border-border rounded-2xl p-6 mb-6"
        data-testid="multi-currency-summary-loading"
      >
        <div className="text-sm text-muted-foreground">
          Loading multi-currency rollup…
        </div>
      </section>
    );
  }
  if (!data) return null;

  const rollup = data.rollup || {};
  const breakdown = data.native_breakdown || {};
  const display = data.display_currency || plan.currency;
  const fx = data.fx || {};

  const currencies = Object.keys(breakdown).sort((a, b) => {
    // Anchor the plan currency at the top, then USD/display, then alpha.
    if (a === data.plan_currency) return -1;
    if (b === data.plan_currency) return 1;
    if (a === display) return -1;
    if (b === display) return 1;
    return a.localeCompare(b);
  });

  return (
    <section
      className="bg-card border border-border rounded-2xl p-6 mb-6"
      data-testid="multi-currency-summary"
    >
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground inline-flex items-center gap-2">
            <Globe size={12} /> Multi-currency rollup
          </div>
          <div className="mt-2">
            <div className="text-sm text-muted-foreground">
              You hold values in {currencies.length} currencies. Original
              amounts stay untouched; the rollup converts to your display
              currency using the latest FX rates.
            </div>
          </div>
        </div>
        <DisplayCurrencyPicker
          currencies={currencies}
          value={display}
          onChange={setDisplayCcy}
          onRefresh={() => fetchRollup(displayCcy)}
          loading={loading}
        />
      </div>

      {/* --- GLOBAL ROLLUP HEADLINE --- */}
      <div
        className="rounded-xl border border-primary/30 bg-primary/5 p-5 mb-6"
        data-testid="multi-currency-rollup-headline"
      >
        <div className="text-xs font-mono uppercase tracking-widest text-primary/80">
          Global net worth · in {display}
        </div>
        <div
          className="mt-2 font-display text-4xl font-black tabular-nums"
          data-testid="multi-currency-rollup-networth"
        >
          <Money amount={rollup.net_worth} currency={display} />
        </div>
        <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1.5">
          <Info size={12} />
          <span>
            Estimated · rates as of {formatAge(fx.timestamp)}
            {fx.stale ? " · using cached rates" : ""}
          </span>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <RollupStat
            label="Assets"
            value={rollup.total_assets}
            currency={display}
            tone="good"
            testId="multi-currency-rollup-assets"
          />
          <RollupStat
            label="Liabilities"
            value={rollup.total_liabilities}
            currency={display}
            tone="bad"
            testId="multi-currency-rollup-liabilities"
          />
          <RollupStat
            label="Annual income"
            value={rollup.total_income_annual}
            currency={display}
            testId="multi-currency-rollup-income"
          />
          <RollupStat
            label="Monthly expenses"
            value={rollup.total_expenses_monthly}
            currency={display}
            testId="multi-currency-rollup-expenses"
          />
        </div>
      </div>

      {/* --- NATIVE BREAKDOWN --- */}
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">
          Native currency breakdown (stored values)
        </div>
        <div className="space-y-2" data-testid="multi-currency-breakdown">
          {currencies.map((c) => (
            <NativeRow key={c} currency={c} bucket={breakdown[c]} isPlan={c === data.plan_currency} />
          ))}
        </div>
      </div>
    </section>
  );
}

function DisplayCurrencyPicker({ currencies, value, onChange, onRefresh, loading }) {
  return (
    <div className="flex items-center gap-2">
      <label
        htmlFor="mc-display-select"
        className="text-xs font-mono uppercase tracking-widest text-muted-foreground"
      >
        Display in
      </label>
      <select
        id="mc-display-select"
        data-testid="multi-currency-display-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-sm bg-background border border-border rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary/40"
      >
        {currencies.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
      <button
        type="button"
        data-testid="multi-currency-refresh"
        onClick={onRefresh}
        disabled={loading}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
      >
        <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
        <span className="hidden sm:inline">Refresh</span>
      </button>
    </div>
  );
}

function RollupStat({ label, value, currency, tone, testId }) {
  const toneClass =
    tone === "good"
      ? "text-emerald-500"
      : tone === "bad"
        ? "text-destructive"
        : "";
  return (
    <div className="rounded-lg bg-background/60 border border-border/60 p-3" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className={`font-display text-lg font-bold tabular-nums mt-1 ${toneClass}`}>
        <Money amount={value} currency={currency} />
      </div>
    </div>
  );
}

function NativeRow({ currency, bucket, isPlan }) {
  const nw = bucket?.net_worth || 0;
  const nwPositive = nw >= 0;
  return (
    <div
      className="flex items-center gap-4 py-2.5 border-b border-border/40 last:border-b-0"
      data-testid={`multi-currency-row-${currency}`}
    >
      <div className="w-16 shrink-0">
        <div className="inline-flex items-center gap-1.5">
          <span className="font-mono text-sm font-semibold tracking-wider">
            {currency}
          </span>
          {isPlan && (
            <span
              className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-primary/15 text-primary"
              data-testid={`plan-anchor-${currency}`}
            >
              Plan
            </span>
          )}
        </div>
      </div>
      <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <NativeCell
          label="Assets"
          value={bucket.assets}
          currency={currency}
          className="text-emerald-500"
        />
        <NativeCell
          label="Liabilities"
          value={bucket.liabilities}
          currency={currency}
          className="text-destructive"
        />
        <NativeCell
          label="Income /yr"
          value={bucket.income_annual}
          currency={currency}
        />
        <NativeCell
          label="Expenses /mo"
          value={bucket.expenses_monthly}
          currency={currency}
        />
      </div>
      <div className="w-32 text-right shrink-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          Net worth
        </div>
        <div className={`font-display font-bold tabular-nums ${nwPositive ? "" : "text-destructive"}`}>
          <Money amount={nw} currency={currency} />
        </div>
      </div>
    </div>
  );
}

function NativeCell({ label, value, currency, className = "" }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className={`font-semibold tabular-nums ${className}`}>
        <Money amount={value} currency={currency} />
      </div>
    </div>
  );
}
